import { Modal, ModalBody, ModalFooter } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Avatar, {
  AvatarSize,
  AvatarStatus
} from '../../../../components/Avatar/Avatar';
import Blockquote from '../../../../components/Blockquote';
import Button from '../../../../components/Button/Button';
import ModalHeader from '../../../../components/ModalHeader';
import useMobile from '../../../../hooks/useMobileHook';
import { VERIFY_DEPENDENT_ROUTE } from '../../../../routes/types';
import { getAllProfiles } from '../../../../services/profiles';

export type customPendingBeneficiariesModalProps = {
  showBeneficiariesModal: boolean;
  handleCloseBeneficiariesModal: () => void;
};

const PendingBeneficiariesModal = ({
  showBeneficiariesModal,
  handleCloseBeneficiariesModal,
}: customPendingBeneficiariesModalProps) => {
  const isMobile = useMobile();
  const primaryProfile = getAllProfiles().data.filter(
    (profile) => profile.relationship === 'spouse'
  )[0];

  const secondaryProfiles = getAllProfiles().data.filter(
    (profile, idx) => idx > 1
  );

  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate(VERIFY_DEPENDENT_ROUTE);
    handleCloseBeneficiariesModal();
  };

  return (
    <Modal
      fullscreen={isMobile}
      show={showBeneficiariesModal}
      onHide={handleCloseBeneficiariesModal}
      dialogClassName="modal-xl"
    >
      <ModalHeader closeButton>
        <h4 className="text-sm-20-lg-32">Pending Beneficiaries</h4>
      </ModalHeader>
      <ModalBody>
        <section>
          <Blockquote variant="warning-bg" lineVariant="warning">
            <div className={'mb-0 text-ligther-grey fw-bolder'}>
              <span>Beneficiary Update Pending</span>
            </div>
            <div className={'mb-0 text-ligther-grey'}>
              <span>
                You are required to complete, sign and return a Beneficiary
                Designation Authorization Form. You can request another copy of
                the form via mail or you can print it yourself. After filling
                the form, upload or mail it back to complete the process.
              </span>
            </div>
          </Blockquote>
        </section>
        <section className="mt-10 mt-lg-12 border rounded">
          <div className="pt-11 px-10 pt-lg-11 px-lg-10 d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center">
            <h5 className="text-sm-20-lg-24">Basic Life Insurance</h5>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between pt-11 px-10 pt-lg-11 px-lg-10 pb-11">
            <section className="col-12 col-lg-6 pe-lg-9">
              <div className="border-bottom">
                <h6 className="fw-light">Primary</h6>
              </div>
              <div className="d-flex flex-row justify-content-lg-between mt-4 mt-lg-10">
                <div className="col-1 me-7 d-flex align-items-center">
                  <Avatar
                    id={primaryProfile.id}
                    image={primaryProfile.image}
                    status={AvatarStatus.ENABLED}
                    size={AvatarSize.SMALL}
                    name={primaryProfile.name}
                  />
                </div>
                <div className="d-flex flex-column col-9">
                  <span className="text-sub1 fw-light lh-xxs">
                    {primaryProfile.name}
                  </span>
                  <span className="text-sub1 fw-light text-ligther-grey lh-xxs">
                    {primaryProfile.relationship}
                  </span>
                </div>
                <div className="col-1 align-items-end">
                  <span className="text-sub1 text-primary fw-normal lh-xxs">
                    100%
                  </span>
                </div>
              </div>
            </section>
            <section className="col-12 col-lg-6 mt-12 mt-lg-0  ps-lg-9">
              <div className="border-bottom">
                <h6 className="fw-light">Contingent</h6>
              </div>

              {secondaryProfiles.map((secondaryProfile) => {
                return (
                  <div className="d-flex flex-row justify-content-lg-between mt-4 mt-lg-10">
                    <div className="col-1 me-7 d-flex align-items-center">
                      <Avatar
                        id={secondaryProfile.id}
                        image={secondaryProfile.image}
                        status={AvatarStatus.ENABLED}
                        size={AvatarSize.SMALL}
                        name={secondaryProfile.name}
                      />
                    </div>
                    <div className="d-flex flex-column col-9">
                      <span className="text-sub1 fw-light lh-xxs">
                        {secondaryProfile.name}
                      </span>
                      <span className="text-sub1 fw-light text-ligther-grey lh-xxs">
                        {secondaryProfile.relationship}
                      </span>
                    </div>
                    <div className="col-1 align-items-end">
                      <span className="text-sub1 text-primary fw-normal lh-xxs">
                        {`${(100 / secondaryProfiles.length).toFixed(2).replace(/\.00/,'')}%`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </section>
          </div>
        </section>
        <section className="mt-10 mt-lg-12 border rounded">
          <div className="pt-11 px-10 pt-lg-11 px-lg-10 d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center">
            <h5 className="text-sm-20-lg-24">HSA Investment</h5>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between pt-11 px-10 pt-lg-11 px-lg-10 pb-11">
            <section className="col-12 col-lg-6 pe-lg-9">
              <div className="border-bottom">
                <h6 className="fw-light">Primary</h6>
              </div>
              <div className="d-flex flex-row justify-content-between mt-4 mt-lg-10">
                <div className="col-1 me-7 d-flex align-items-center">
                  <Avatar
                    id={primaryProfile.id}
                    image={primaryProfile.image}
                    status={AvatarStatus.ENABLED}
                    size={AvatarSize.SMALL}
                    name={primaryProfile.name}
                  />
                </div>
                <div className="d-flex flex-column col-9">
                  <span className="text-sub1 fw-light lh-xxs">
                    {primaryProfile.name}
                  </span>
                  <span className="text-sub1 fw-light text-ligther-grey lh-xxs">
                    {primaryProfile.relationship}
                  </span>
                </div>
                <div className="col-1 align-items-end">
                  <span className="text-sub1 text-primary fw-normal lh-xxs">
                    100%
                  </span>
                </div>
              </div>
            </section>
            <section className="col-12 col-lg-6 mt-12 mt-lg-0 ps-lg-9">
              <div className="border-bottom">
                <h6 className="fw-light">Contingent</h6>
              </div>

              {secondaryProfiles.map((secondaryProfile) => {
                return (
                  <div className="d-flex flex-row justify-content-lg-between mt-4 mt-lg-10">
                    <div className="col-1 me-7 d-flex align-items-center">
                      <Avatar
                        id={secondaryProfile.id}
                        image={secondaryProfile.image}
                        status={AvatarStatus.ENABLED}
                        size={AvatarSize.SMALL}
                        name={secondaryProfile.name}
                      />
                    </div>
                    <div className="d-flex flex-column col-9">
                      <span className="text-sub1 fw-light lh-xxs">
                        {secondaryProfile.name}
                      </span>
                      <span className="text-sub1 fw-light text-ligther-grey lh-xxs">
                        {secondaryProfile.relationship}
                      </span>
                    </div>
                    <div className="col-1 align-items-end">
                      <span className="text-sub1 text-primary fw-normal lh-xxs">
                        {`${(100 / secondaryProfiles.length).toFixed(2).replace(/\.00/,'')}%`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </section>
          </div>
        </section>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary-outline"
          className={'order-1 order-lg-3 mt-7 mt-lg-0'}
          onClick={handleCloseBeneficiariesModal}
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PendingBeneficiariesModal;
